import { router } from '@/router'
import Network from '@/Network'
import { getQueryString } from '@/utils/string'
import { defineStore } from 'pinia'
import { reactive } from 'vue'
import type { LocationQuery } from 'vue-router'
import { parseQuery, useRoute } from 'vue-router'
import { format, addDays } from 'date-fns'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import type { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import { parseCoverageStatuses } from '@/utils/coverageFilters'
import {
  useDoctorReportsStore,
  useStationsApproveStore,
  useStationStore
} from '@/pinia-store/useStationStore'

export type FilterStates = {
  company?: number | null
  healthUnits?: number[] | null
  schedules?: number[] | null
  positions?: number[] | null
  doctors?: number[] | null
  startDate?: Date | string
  endDate?: Date | string
  crmStates?: string[] | null
  crms?: string[] | null
  valueMin?: number | null
  valueMax?: number | null
  paymentValueMin?: number | null
  paymentValueMax?: number | null
  withCheckin?: boolean | null
  withCheckout?: boolean | null
  stationType?: string | null
  inCash?: boolean
  advanceRequested?: boolean
  advancePaid?: boolean
  ourTeam?: boolean
  status?: string[] | null
  weekDays?: number[] | null
  operationalGroups?: string[] | null
  openedStation?: boolean | null
  startHour?: string[] | null
  endHour?: string[] | null
  shifts?: number[] | null
  weekOfMonth?: number[] | null
  stationWeekParity?: number | null
  isOpenDoctorStation?: boolean | null
  doctorName?: string | null
  sectorIds?: number[] | null
  /** Especialidades oficiais do plantão (spec §4.7). */
  specialtyIds?: number[] | null
  /** Especialidades da empresa do plantão (spec §4.7). */
  companySpecialtyIds?: number[] | null
  doctorStatus?: boolean | null
  hasException?: boolean | null
  /** Situação de cobertura do plantão; serializada como CSV na camada Network. */
  coverageStatus?: CoverageStatus[] | null
}

const DEFAULT_VALUES = {
  positions: undefined,
  company: undefined,
  healthUnits: undefined,
  schedules: undefined,
  doctors: undefined,
  startDate: undefined,
  endDate: undefined,
  crmStates: undefined,
  crms: undefined,
  valueMin: undefined,
  valueMax: undefined,
  paymentValueMin: undefined,
  paymentValueMax: undefined,
  withCheckin: undefined,
  withCheckout: undefined,
  inCash: undefined,
  advanceRequested: undefined,
  advancePaid: undefined,
  status: undefined,
  stationType: undefined,
  weekDays: undefined,
  operationalGroups: undefined,
  openedStation: undefined,
  startHour: undefined,
  endHour: undefined,
  shifts: undefined,
  weekOfMonth: undefined,
  stationWeekParity: undefined,
  isOpenDoctorStation: undefined,
  doctorName: undefined,
  sectorIds: undefined,
  specialtyIds: undefined,
  companySpecialtyIds: undefined,
  doctorStatus: undefined,
  hasException: undefined,
  coverageStatus: undefined
}

export const filterStates: FilterStates = reactive({ ...DEFAULT_VALUES })

// A query string serializa listas com `Array.prototype.toString` (`a,b`) e tudo
// o mais como texto. Sem reidratar cada campo com o seu tipo, `filters` volta da
// URL como string crua: os chips de `filters.used.vue` quebram ao chamar `.map`
// num valor unico e o backend recebe tipos que nao batem com o `FilterStates`.
const NUMBER_LIST_KEYS = [
  'healthUnits',
  'schedules',
  'positions',
  'doctors',
  'shifts',
  'weekDays',
  'weekOfMonth',
  'sectorIds',
  'specialtyIds',
  'companySpecialtyIds'
] as const
const TEXT_LIST_KEYS = [
  'crms',
  'crmStates',
  'status',
  'operationalGroups',
  'startHour',
  'endHour',
  'coverageStatus'
] as const
const NUMBER_KEYS = [
  'company',
  'valueMin',
  'valueMax',
  'paymentValueMin',
  'paymentValueMax',
  'stationWeekParity'
] as const
const BOOLEAN_KEYS = [
  'withCheckin',
  'withCheckout',
  'inCash',
  'advanceRequested',
  'advancePaid',
  'ourTeam',
  'openedStation',
  'isOpenDoctorStation',
  'doctorStatus',
  'hasException'
] as const

const FILTER_KEYS = Object.keys(DEFAULT_VALUES) as (keyof FilterStates)[]
/** A11: chaves que a base não conhecia — com a flag desligada a URL não as reidrata. */
const DASHBOARD_V2_FILTER_KEYS: (keyof FilterStates)[] = ['specialtyIds', 'companySpecialtyIds']

const toTextList = (value: unknown): string[] =>
  (Array.isArray(value) ? value : String(value).split(','))
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)

const toNumberList = (value: unknown): number[] =>
  toTextList(value)
    .map(Number)
    .filter((id) => !Number.isNaN(id))

const toBoolean = (value: unknown): boolean | undefined => {
  if (value === 'true' || value === true) return true
  if (value === 'false' || value === false) return false
  return undefined
}

const toNumber = (value: unknown): number | undefined => {
  const parsed = Number(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

/** Converte a query string da rota nos tipos declarados em `FilterStates`. */
export function parseFiltersFromQuery(query: LocationQuery): FilterStates {
  const filters: Record<string, unknown> = {}
  const keys = useFeatureFlags().flags.enabledDashboardV2
    ? FILTER_KEYS
    : FILTER_KEYS.filter((key) => !DASHBOARD_V2_FILTER_KEYS.includes(key))

  keys.forEach((key) => {
    const raw = (query as Record<string, unknown>)[key]
    if (raw === undefined || raw === null || raw === '') return

    if ((NUMBER_LIST_KEYS as readonly string[]).includes(key)) filters[key] = toNumberList(raw)
    else if ((TEXT_LIST_KEYS as readonly string[]).includes(key)) filters[key] = toTextList(raw)
    else if ((NUMBER_KEYS as readonly string[]).includes(key)) filters[key] = toNumber(raw)
    else if ((BOOLEAN_KEYS as readonly string[]).includes(key)) filters[key] = toBoolean(raw)
    else filters[key] = raw
  })

  // Só os status do contrato, na ordem canônica (lixo na URL = sem filtro).
  if (filters.coverageStatus) filters.coverageStatus = parseCoverageStatuses(filters.coverageStatus)

  Object.keys(filters).forEach((key) => {
    const value = filters[key]
    if (value === undefined || (Array.isArray(value) && value.length === 0)) delete filters[key]
  })

  return filters as FilterStates
}

/**
 * `new Date('2026-09-09')` e interpretado como meia-noite UTC, o que cai no dia
 * anterior em fusos negativos (America/Sao_Paulo). Somar um dia devolve a data
 * ao dia escrito na URL quando ela volta a ser formatada com `yyyy-MM-dd`.
 */
const parseQueryDate = (value: unknown): Date | undefined => {
  if (!value) return undefined
  const parsed = new Date(String(value))
  return Number.isNaN(parsed.getTime()) ? undefined : addDays(parsed, 1)
}

const withoutCoverageStatus = (filters: FilterStates): FilterStates => {
  const rest = { ...filters }
  delete rest.coverageStatus
  return rest
}

export type FilterStationsState = {
  filters: FilterStates
}
export const useFilterStationsStore = defineStore('filterStations', {
  state: (): FilterStationsState => ({
    filters: {}
  }),
  actions: {
    async initialRequest() {
      this.setFiltersFromQuery()
      await this.updateAllStores()
    },
    async setFilters() {
      this.filters = {
        ...filterStates,
        startDate:
          filterStates.startDate && typeof filterStates.startDate !== 'string'
            ? format(filterStates.startDate, 'yyyy-MM-dd')
            : undefined,
        endDate:
          filterStates.endDate && typeof filterStates.endDate !== 'string'
            ? format(filterStates.endDate, 'yyyy-MM-dd')
            : undefined
      }

      // Backend espera 'crmsUf', nao 'crmStates'
      if (this.filters.crmStates) {
        ;(this.filters as Record<string, any>).crmsUf = this.filters.crmStates
        delete this.filters.crmStates
      }

      // D-B1: no BE `openedStation=false`/`isOpenDoctorStation=false` já são DOCTOR ∪ UNIDENTIFIED;
      // com chips de cobertura os legados não viajam (os dois juntos fariam AND).
      if (this.filters.coverageStatus?.length) {
        delete this.filters.openedStation
        delete this.filters.isOpenDoctorStation
        filterStates.openedStation = undefined
        filterStates.isOpenDoctorStation = undefined
      }

      const paramsForQuery: Record<string, any> = { ...this.filters }

      if (paramsForQuery.isOpenDoctorStation === false) {
        paramsForQuery.isOpenDoctorStation = 'false'
      }

      if (paramsForQuery.doctorStatus === false) {
        paramsForQuery.doctorStatus = 'false'
      }

      if (paramsForQuery.hasException === false) {
        paramsForQuery.hasException = 'false'
      }

      const qs = getQueryString(paramsForQuery)
      router.replace({ query: parseQuery(qs) })
      await this.updateAllStores()
    },
    /**
     * Reidrata os filtros a partir da query da rota. Query sem nenhum filtro
     * conhecido nao mexe no store — abrir a tela sem query nao pode apagar o
     * que o usuario ja aplicou pelo modal.
     */
    setFiltersFromQuery(query?: LocationQuery) {
      const routeQuery = query ?? useRoute()?.query ?? {}
      const filters = parseFiltersFromQuery(routeQuery as LocationQuery)

      if (!Object.keys(filters).length) return

      this.filters = filters

      Object.assign(filterStates, {
        ...DEFAULT_VALUES,
        ...filters,
        startDate: parseQueryDate(filters.startDate),
        endDate: parseQueryDate(filters.endDate)
      })
    },
    async clearFilters() {
      const useStation = useStationStore()
      router.replace({ query: {} })
      this.filters = { ...DEFAULT_VALUES }
      Object.assign(filterStates, DEFAULT_VALUES)
      await this.updateAllStores()
      useStation.clearConsulting()
    },
    async updateAllStores() {
      const useStationsApprove = useStationsApproveStore()
      const useDoctorReports = useDoctorReportsStore()
      const useStation = useStationStore()

      const consultingQuery = { page: 1, perPage: 10, sort: [], columnFilters: this.filters }
      // A lista da Consulta e o relatório de médicos pedem o MESMO GET /stations/consulting:
      // um request só, compartilhado (antes saíam 2 idênticos a cada "Aplicar filtros").
      const sharedConsulting = this.hasPeriod
        ? Network.station.getConsultingList(consultingQuery)
        : undefined

      await Promise.allSettled([
        // Sem periodo a consulta e invalida: pedi-la so devolveria o erro cru do
        // backend ("startDate and endDate are required for consulting") como
        // estado de erro da aba.
        this.hasPeriod
          ? useStation.getListConsultingStations(consultingQuery, sharedConsulting)
          : Promise.resolve(useStation.clearConsulting()),
        useStationsApprove.getStationsToApprove({
          page: 1,
          perPage: 10,
          sort: [],
          // A aprovação não tem os chips de cobertura (M10-A): o filtro não viaja para lá.
          columnFilters: withoutCoverageStatus(this.filters)
        }),
        useDoctorReports.getDoctorReports(consultingQuery, sharedConsulting)
      ])
    }
  },
  getters: {
    getFilters(): FilterStates {
      return this.filters
    },
    /** A consulta de plantoes exige periodo: sem ele nao ha o que pedir a API. */
    hasPeriod(): boolean {
      return Boolean(this.filters.startDate && this.filters.endDate)
    }
  }
})
